import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

export interface PickListOption {
  value: string;
  label: string;
}

interface PickListProps {
  label?: string;
  placeholder: string;
  value: string;
  options: PickListOption[];
  disabled?: boolean;
  autoOpen?: boolean;
  searchable?: boolean;
  onChange: (value: string) => void;
  onDismiss?: () => void;
}

export default function PickList({ label, placeholder, value, options, disabled = false, autoOpen = false, searchable = false, onChange, onDismiss }: PickListProps) {
  const [open, setOpen] = useState(autoOpen);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const selected = options.find((option) => option.value === value);
  const visible = searchable && query.trim()
    ? options.filter((option) => option.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        onDismiss?.();
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        onDismiss?.();
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onDismiss]);

  return (
    <div ref={rootRef} className="relative">
      {label ? <span className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</span> : null}
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((current) => !current)}
        className={`flex w-full items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2 text-left text-sm disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400 dark:bg-gray-950 dark:disabled:bg-gray-900 dark:disabled:text-gray-500 ${
          label ? 'mt-1' : ''
        } ${open ? 'border-gray-900 ring-2 ring-gray-900/10 dark:border-white/30' : 'border-gray-300 dark:border-white/15'} ${selected ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}`}
      >
        <span className="truncate">{selected?.label ?? placeholder}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-gray-500 transition ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-40 mt-1 max-h-60 min-w-full w-max max-w-[min(36rem,calc(100vw-2rem))] overflow-y-auto rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-950 py-1 shadow-lg"
        >
          {searchable ? (
            <li className="px-2 py-1">
              <input
                autoFocus
                value={query}
                placeholder="Type a year"
                onChange={(event) => setQuery(event.target.value)}
                className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm dark:border-white/10 dark:bg-gray-900"
              />
            </li>
          ) : null}
          {visible.map((option) => {
            const active = option.value === value;
            return (
              <li key={option.value} role="option" aria-selected={active}>
                <button
                  type="button"
                  className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm ${
                    active ? 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100' : 'text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-white/5'
                  }`}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  <span>{option.label}</span>
                  {active ? <Check className="h-4 w-4 text-emerald-700" aria-hidden="true" /> : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
